import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Building2, Sparkles, Phone, ArrowRight, ChevronDown, UtensilsCrossed, Shirt, Flame, Refrigerator, SunDim, Microwave, DoorOpen, Warehouse, Wind, BedDouble, Trash2, Fence, Sparkles as SparklesIcon, Package, Minus, Plus } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import { ServiceNav } from '../components/ServiceNav';
const cardClass = 'bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-sm';
const stepHeadingClass = 'font-bebas text-2xl md:text-3xl text-[#1a2744] pb-3 border-b border-gray-200';
const labelClass = 'text-[#1a2744] font-medium text-sm';
const inputClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all placeholder-gray-400';
const selectClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all appearance-none cursor-pointer';
type PillGroupProps = {
  options: string[];
  selected: string | string[];
  onSelect: (val: string) => void;
  multi?: boolean;
};
function PillGroup({
  options,
  selected,
  onSelect,
  multi = false
}: PillGroupProps) {
  const isSelected = (opt: string) => multi ? (selected as string[]).includes(opt) : selected === opt;
  return <div className="flex flex-wrap gap-3" data-id="element-1191">
      {options.map(opt => <button key={opt} type="button" onClick={() => onSelect(opt)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${isSelected(opt) ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-1192">
          {opt}
        </button>)}
    </div>;
}
const extras = [{
  name: 'Airbnb Combo Package',
  price: 7,
  icon: Package
}, {
  name: 'Washing dishes',
  price: 7,
  icon: UtensilsCrossed
}, {
  name: 'Laundry (wash & fold)',
  price: 7,
  icon: Shirt
}, {
  name: 'Inside of oven',
  price: 7,
  icon: Flame
}, {
  name: 'Inside of fridge',
  price: 7,
  icon: Refrigerator
}, {
  name: 'Inside facade of windows',
  price: 7,
  icon: SunDim
}, {
  name: 'Inside microwave',
  price: 7,
  icon: Microwave
}, {
  name: 'Interior of Kitchen Cabinets',
  price: 7,
  icon: DoorOpen
}, {
  name: 'Finished Basement',
  price: 7,
  icon: Warehouse
}, {
  name: 'Flu Buster - Eucalyptus',
  price: 7,
  icon: Wind
}, {
  name: 'Change Sheets',
  price: 7,
  icon: BedDouble
}, {
  name: 'Property Cleanup',
  price: 7,
  icon: Trash2
}, {
  name: 'Patio or Balcony',
  price: 7,
  icon: Fence
}, {
  name: 'Carpet Steam Clean - Entire Home',
  price: 7,
  icon: SparklesIcon
}, {
  name: 'Area Rugs Steam Cleaning',
  price: 7,
  icon: SparklesIcon
}];
const timeSlots = ['14:00', '15:00'];
export function PostRenovationQuote() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Step 1 - Contact
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({
      ...contact,
      [e.target.name]: e.target.value
    });
  };
  // Step 2 - Location
  const [location, setLocation] = useState({
    address: '',
    unit: '',
    city: '',
    postalCode: ''
  });
  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation({
      ...location,
      [e.target.name]: e.target.value
    });
  };
  // Step 3 - Cleaning details
  const [hours, setHours] = useState('2 Hours');
  const [cleaners, setCleaners] = useState('2 Cleaners');
  const [propertyType, setPropertyType] = useState('Cottage/Townhouse/Bungalow');
  const [bedrooms, setBedrooms] = useState('Studio / 1 Bedroom');
  const [bathrooms, setBathrooms] = useState('1 Bathroom');
  const [kitchens, setKitchens] = useState('1 Kitchen');
  const [floors, setFloors] = useState(1);
  const [sqFeet, setSqFeet] = useState('less than 1600');
  const [access, setAccess] = useState('I will meet you at the location');
  const [homeStatus, setHomeStatus] = useState('Completely Empty');
  const [parking, setParking] = useState('Free on street');
  // Step 4 - Extras
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const toggleExtra = (name: string) => {
    setSelectedExtras(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);
  };
  // Step 5 - Date & Time
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  // Step 6 - Access details
  const [accessDetails, setAccessDetails] = useState('');
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-1193">
      <SEOHead title="Book Post Renovation Cleaning | Smith & Jengs Cleaning Services" description="Book your Post Renovation Cleaning in Bicester and Oxfordshire. Fast, reliable builders clean in 60 seconds." canonical="https://smithandjengs.com/post-renovation-quote" data-id="element-1194" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Post Renovation',
      url: 'https://smithandjengs.com/post-renovation-quote'
    }, {
      name: 'Book Online',
      url: 'https://smithandjengs.com/post-renovation-quote'
    }]} data-id="element-1195" />
      <Navbar data-id="element-1196" />

      <main className="flex-1 pt-24" data-id="element-1197">
        <ServiceNav data-id="element-1198" />

        {/* Header */}
        <section className="w-full bg-white pt-12 md:pt-16 pb-12 px-4 md:px-12" data-id="element-1199">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl w-full mx-auto text-center" data-id="element-1200">
            <h1 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-6" data-id="element-1201">
              <span className="text-5xl md:text-6xl lg:text-7xl block" data-id="element-1202">
                BOOK ONLINE
              </span>
              <span className="text-3xl md:text-4xl lg:text-5xl block text-[#e91e8c]" data-id="element-1203">
                POST RENOVATION
              </span>
            </h1>
            <p className="text-gray-600 font-medium max-w-2xl mx-auto text-lg" data-id="element-1204">
              Book your Post Renovation Cleaning in 60 seconds.
            </p>
          </motion.div>
        </section>

        {/* Form Section */}
        <section className="w-full bg-[#f8f9fa] py-12 md:py-16 px-4 md:px-8" id="booking-form" data-id="element-1205">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-4xl w-full mx-auto" data-id="element-1206">
            <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-1207">
              {/* STEP 1: Contact */}
              <div className={cardClass} data-id="element-1208">
                <h3 className={stepHeadingClass} data-id="element-1209">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1210">STEP 1:</span> Contact
                  Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1211">
                  <div className="space-y-2" data-id="element-1212">
                    <label className={labelClass} data-id="element-1213">First Name</label>
                    <input type="text" name="firstName" value={contact.firstName} onChange={handleContactChange} className={inputClass} placeholder="John" data-id="element-1214" />
                  </div>
                  <div className="space-y-2" data-id="element-1215">
                    <label className={labelClass} data-id="element-1216">Last Name</label>
                    <input type="text" name="lastName" value={contact.lastName} onChange={handleContactChange} className={inputClass} placeholder="Doe" data-id="element-1217" />
                  </div>
                  <div className="space-y-2" data-id="element-1218">
                    <label className={labelClass} data-id="element-1219">Email</label>
                    <input type="email" name="email" value={contact.email} onChange={handleContactChange} className={inputClass} placeholder="john@example.com" data-id="element-1220" />
                  </div>
                  <div className="space-y-2" data-id="element-1221">
                    <label className={labelClass} data-id="element-1222">Phone Number</label>
                    <div className="flex" data-id="element-1223">
                      <div className="flex items-center justify-center px-4 border border-r-0 border-gray-200 rounded-l-lg bg-gray-50 text-gray-500" data-id="element-1224">
                        🇬🇧
                      </div>
                      <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} className={`${inputClass} rounded-l-none`} placeholder="07000 000000" data-id="element-1225" />
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 2: Location */}
              <div className={cardClass} data-id="element-1226">
                <h3 className={stepHeadingClass} data-id="element-1227">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1228">STEP 2:</span> Cleaning
                  Location
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1229">
                  <div className="space-y-2" data-id="element-1230">
                    <label className={labelClass} data-id="element-1231">Address</label>
                    <input type="text" name="address" value={location.address} onChange={handleLocationChange} className={inputClass} placeholder="Enter a location" data-id="element-1232" />
                  </div>
                  <div className="space-y-2" data-id="element-1233">
                    <label className={labelClass} data-id="element-1234">Unit/Apt/Suite #</label>
                    <input type="text" name="unit" value={location.unit} onChange={handleLocationChange} className={inputClass} placeholder="Optional" data-id="element-1235" />
                  </div>
                  <div className="space-y-2" data-id="element-1236">
                    <label className={labelClass} data-id="element-1237">City</label>
                    <input type="text" name="city" value={location.city} onChange={handleLocationChange} className={inputClass} placeholder="Bicester" data-id="element-1238" />
                  </div>
                  <div className="space-y-2" data-id="element-1239">
                    <label className={labelClass} data-id="element-1240">Postal Code</label>
                    <input type="text" name="postalCode" value={location.postalCode} onChange={handleLocationChange} className={inputClass} placeholder="OX26" data-id="element-1241" />
                  </div>
                </div>
              </div>

              {/* STEP 3: Details */}
              <div className={cardClass} data-id="element-1242">
                <h3 className={stepHeadingClass} data-id="element-1243">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1244">STEP 3:</span> Tell us
                  about the cleaning
                </h3>
                <div className="space-y-8 mt-8" data-id="element-1245">
                  <div className="space-y-4" data-id="element-1246">
                    <label className={labelClass} data-id="element-1247">
                      How many hours and cleaners would you like to book?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1248">
                      <div className="relative" data-id="element-1249">
                        <select value={hours} onChange={e => setHours(e.target.value)} className={selectClass} data-id="element-1250">
                          <option data-id="element-1251">2 Hours</option>
                          <option data-id="element-1252">3 Hours</option>
                          <option data-id="element-1253">4 Hours</option>
                          <option data-id="element-1254">5 Hours</option>
                          <option data-id="element-1255">6 Hours</option>
                          <option data-id="element-1256">7 Hours</option>
                          <option data-id="element-1257">8 Hours</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1258" />
                      </div>
                      <div className="relative" data-id="element-1259">
                        <select value={cleaners} onChange={e => setCleaners(e.target.value)} className={selectClass} data-id="element-1260">
                          <option data-id="element-1261">1 Cleaner</option>
                          <option data-id="element-1262">2 Cleaners</option>
                          <option data-id="element-1263">3 Cleaners</option>
                          <option data-id="element-1264">4 Cleaners</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1265" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1266">
                    <label className={labelClass} data-id="element-1267">Property type</label>
                    <div className="relative" data-id="element-1268">
                      <select value={propertyType} onChange={e => setPropertyType(e.target.value)} className={selectClass} data-id="element-1269">
                        <option data-id="element-1270">Cottage/Townhouse/Bungalow</option>
                        <option data-id="element-1271">Detached House</option>
                        <option data-id="element-1272">Semi-Detached</option>
                        <option data-id="element-1273">Flat/Apartment</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1274" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1275">
                    <div className="space-y-2" data-id="element-1276">
                      <label className={labelClass} data-id="element-1277">Bedrooms</label>
                      <div className="relative" data-id="element-1278">
                        <select value={bedrooms} onChange={e => setBedrooms(e.target.value)} className={selectClass} data-id="element-1279">
                          <option data-id="element-1280">Studio / 1 Bedroom</option>
                          <option data-id="element-1281">2 Bedrooms</option>
                          <option data-id="element-1282">3 Bedrooms</option>
                          <option data-id="element-1283">4 Bedrooms</option>
                          <option data-id="element-1284">5+ Bedrooms</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1285" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-1286">
                      <label className={labelClass} data-id="element-1287">Bathrooms</label>
                      <div className="relative" data-id="element-1288">
                        <select value={bathrooms} onChange={e => setBathrooms(e.target.value)} className={selectClass} data-id="element-1289">
                          <option data-id="element-1290">1 Bathroom</option>
                          <option data-id="element-1291">2 Bathrooms</option>
                          <option data-id="element-1292">3 Bathrooms</option>
                          <option data-id="element-1293">4+ Bathrooms</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1294" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-1295">
                    <label className={labelClass} data-id="element-1296">Kitchens</label>
                    <PillGroup options={['1 Kitchen', '2 Kitchens', '3 Kitchens']} selected={kitchens} onSelect={setKitchens} data-id="element-1297" />
                  </div>

                  <div className="space-y-2" data-id="element-1298">
                    <label className={labelClass} data-id="element-1299">
                      # of floors in the home (incl. Basement)?
                    </label>
                    <div className="flex items-center w-full max-w-[200px]" data-id="element-1300">
                      <button type="button" onClick={() => setFloors(Math.max(1, floors - 1))} className="p-3 border border-gray-200 rounded-l-lg bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors" data-id="element-1301">
                        <Minus className="w-5 h-5" data-id="element-1302" />
                      </button>
                      <input type="number" value={floors} readOnly className="w-full border-y border-gray-200 py-3 text-center bg-white text-[#1a2744] font-medium focus:outline-none" data-id="element-1303" />
                      <button type="button" onClick={() => setFloors(floors + 1)} className="p-3 border border-gray-200 rounded-r-lg bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors" data-id="element-1304">
                        <Plus className="w-5 h-5" data-id="element-1305" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1306">
                    <label className={labelClass} data-id="element-1307">
                      Total Square feet? (incl. Basement)
                    </label>
                    <div className="relative" data-id="element-1308">
                      <select value={sqFeet} onChange={e => setSqFeet(e.target.value)} className={selectClass} data-id="element-1309">
                        <option data-id="element-1310">less than 1600</option>
                        <option data-id="element-1311">1600-2500</option>
                        <option data-id="element-1312">2500-3500</option>
                        <option data-id="element-1313">3500+</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1314" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1315">
                    <label className={labelClass} data-id="element-1316">
                      How do we access the premises?
                    </label>
                    <div className="relative" data-id="element-1317">
                      <select value={access} onChange={e => setAccess(e.target.value)} className={selectClass} data-id="element-1318">
                        <option data-id="element-1319">I will meet you at the location</option>
                        <option data-id="element-1320">Key under mat</option>
                        <option data-id="element-1321">Lock box</option>
                        <option data-id="element-1322">Other</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1323" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1324">
                    <label className={labelClass} data-id="element-1325">Status of the home...</label>
                    <div className="relative" data-id="element-1326">
                      <select value={homeStatus} onChange={e => setHomeStatus(e.target.value)} className={selectClass} data-id="element-1327">
                        <option data-id="element-1328">Completely Empty</option>
                        <option data-id="element-1329">Partially Furnished</option>
                        <option data-id="element-1330">Fully Furnished</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1331" />
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-1332">
                    <label className={labelClass} data-id="element-1333">Parking</label>
                    <PillGroup options={['Free on street', 'Free home parking', 'Parking meters', 'Paid parking lot']} selected={parking} onSelect={setParking} data-id="element-1334" />
                  </div>
                </div>
              </div>

              {/* STEP 4: Extras */}
              <div className={cardClass} data-id="element-1335">
                <h3 className={stepHeadingClass} data-id="element-1336">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1337">STEP 4:</span> Extras?
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-1338">
                  Free up more of your precious time for what matters most!
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-1339">
                  {extras.map(extra => {
                  const isSelected = selectedExtras.includes(extra.name);
                  return <button key={extra.name} type="button" onClick={() => toggleExtra(extra.name)} className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#e91e8c] bg-[#e91e8c]/5' : 'border-gray-100 bg-white hover:border-[#e91e8c]/30 hover:bg-gray-50'}`} data-id="element-1340">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${isSelected ? 'bg-[#e91e8c] text-white' : 'bg-[#1a2744] text-white'}`} data-id="element-1341">
                          <extra.icon className="w-6 h-6" data-id="element-1342" />
                        </div>
                        <span className="text-sm font-medium text-center text-[#1a2744] leading-tight" data-id="element-1343">
                          {extra.name}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* STEP 5: Date & Time */}
              <div className={cardClass} data-id="element-1344">
                <h3 className={stepHeadingClass} data-id="element-1345">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1346">STEP 5:</span> Pick a
                  date for the cleaning.
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-1347">
                  For recurring services, each one will commence on the same day
                  and time as selected for the first service.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1348">
                  <div className="space-y-2" data-id="element-1349">
                    <label className={labelClass} data-id="element-1350">Date</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-id="element-1351" />
                  </div>
                  <div className="space-y-2" data-id="element-1352">
                    <label className={labelClass} data-id="element-1353">Time</label>
                    <div className="relative" data-id="element-1354">
                      <select value={timeSlot} onChange={e => setTimeSlot(e.target.value)} className={selectClass} data-id="element-1355">
                        <option value="" data-id="element-1356">Select a time</option>
                        {timeSlots.map(slot => <option key={slot} value={slot} data-id="element-1357">
                            {slot}
                          </option>)}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1358" />
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 6: Access details */}
              <div className={cardClass} data-id="element-1359">
                <h3 className={stepHeadingClass} data-id="element-1360">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1361">STEP 6:</span> Access
                  details & other info we should know?
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-1362">
                  If your instructions to access the premise was either; 'Pick
                  up Keys' or 'Lock Box', please include address for pick or the
                  code to the lock box here.
                </p>
                <textarea value={accessDetails} onChange={e => setAccessDetails(e.target.value)} className={`${inputClass} min-h-[120px] resize-y`} placeholder="Any special instructions..." data-id="element-1363" />
              </div>

              {/* STEP 7: Submit */}
              <div className="flex justify-center pt-4" data-id="element-1364">
                <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-12 py-4 rounded-full font-semibold tracking-wide transition-colors shadow-md flex items-center gap-3 text-lg" data-id="element-1365">
                  Book Now <ArrowRight className="w-5 h-5" data-id="element-1366" />
                </button>
              </div>
            </form>
          </motion.div>
        </section>

        {/* Explore Our Services */}
        <section className="w-full bg-white py-20 px-4 md:px-12 border-t border-gray-200" data-id="element-1367">
          <div className="max-w-7xl mx-auto" data-id="element-1368">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-1369">
              <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-[#1a2744] tracking-wide" data-id="element-1370">
                EXPLORE OUR <span className="text-[#e91e8c]" data-id="element-1371">SERVICES</span>
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto" data-id="element-1372">
                Discover our full range of professional cleaning solutions
                tailored to your needs.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1373">
              {[{
              title: 'Basic Home Cleaning',
              desc: 'Regular maintenance to keep your home spotless.',
              link: '/basic-home-cleaning',
              icon: Home
            }, {
              title: 'Spring / Deep Cleaning',
              desc: 'Thorough, top-to-bottom detail cleaning.',
              link: '/spring-deep-cleaning',
              icon: Sparkles
            }, {
              title: 'Commercial Cleaning',
              desc: 'Professional cleaning for your business.',
              link: '/commercial-cleaning',
              icon: Building2
            }, {
              title: 'Contact Us',
              desc: 'Get in touch for custom requests.',
              link: '/contact-us',
              icon: Phone
            }].map((service, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} data-id="element-1374">
                  <Link to={service.link} className="group block h-full bg-[#f8f9fa] rounded-xl p-8 hover:shadow-md transition-all border border-gray-100 hover:border-[#5bbcd5]" data-id="element-1375">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-6 group-hover:bg-[#5bbcd5]/10 transition-colors" data-id="element-1376">
                      <service.icon className="w-6 h-6 text-[#1a2744] group-hover:text-[#5bbcd5] transition-colors" data-id="element-1377" />
                    </div>
                    <h3 className="font-bebas text-2xl text-[#1a2744] mb-3 tracking-wide group-hover:text-[#e91e8c] transition-colors" data-id="element-1378">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6 line-clamp-2" data-id="element-1379">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-[#5bbcd5] font-medium text-sm mt-auto" data-id="element-1380">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-1381" />
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1382" />
    </div>;
}